'use client';

import { useState } from 'react';

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  tripDate?: string;
  hasConfirmed?: string;
}

export function WaiverForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    tripDate: '',
    hasConfirmed: false
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<keyof FormErrors, boolean>>({
    firstName: false,
    lastName: false,
    email: false,
    tripDate: false,
    hasConfirmed: false
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [isFormValid, setIsFormValid] = useState(false);

  const validateField = (name: keyof FormErrors, value: string | boolean): string | undefined => {
    if (typeof value === 'string' && !value.trim()) {
      return `${name.charAt(0).toUpperCase() + name.slice(1)} is required`;
    }
    if (name === 'email' && !/\S+@\S+\.\S+/.test(value as string)) {
      return 'Please enter a valid email address';
    }
    if (name === 'tripDate' && !value) {
      return 'Please select your trip date';
    }
    if (name === 'hasConfirmed' && !value) {
      return 'You must accept the waiver terms';
    }
    return undefined;
  };

  const handleFieldChange = (name: keyof FormErrors, value: string | boolean) => {
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    // Update form data and validate
    const newFormData = { ...formData, [name]: value };
    setFormData(newFormData);
    
    const newErrors = {
      firstName: validateField('firstName', newFormData.firstName),
      lastName: validateField('lastName', newFormData.lastName),
      email: validateField('email', newFormData.email),
      tripDate: validateField('tripDate', newFormData.tripDate),
      hasConfirmed: validateField('hasConfirmed', newFormData.hasConfirmed)
    };
    
    setErrors(newErrors);
    setIsFormValid(!Object.values(newErrors).some(error => error !== undefined));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateField('firstName', formData.firstName) &&
        !validateField('lastName', formData.lastName) &&
        !validateField('email', formData.email) &&
        !validateField('tripDate', formData.tripDate) &&
        !validateField('hasConfirmed', formData.hasConfirmed)) {
      setStatus('submitting');
      try {
        const response = await fetch('/api/waivers/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });

        if (!response.ok) throw new Error('Submission failed');
        setStatus('success');
      } catch (error) {
        setStatus('error');
      }
    }
  };

  const inputClasses = (fieldName: keyof FormErrors) => `
    mt-1 block w-full rounded-md shadow-sm p-4 text-md font-sans font-light tracking-wide
    ${errors[fieldName] 
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
      : 'border-gray-300 focus:border-highlight-500 focus:ring-highlight-500'}
  `;

  return (
    <div className="px-6 py-20 pt-16 sm:pb-22 sm:pt-20 lg:px-8 lg:py-24 bg-offwhite-300">
      <h2 className="font-roman text-3xl text-primary-900 tracking-wide text-center mt-0 mb-12">
        Release and Waiver Confirmation Form
      </h2>
      {status === 'success' ? (
        <div className="text-center p-8">
          <h3 className="text-2xl font-roman text-highlight-500 mb-4">Waiver Submitted Successfully!</h3>
          <p className="text-md font-sans tracking-wide">
            Thank you for completing the waiver. Please refresh the page and repeat the process for each participant.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="mx-auto max-w-lg px-6 lg:px-8 space-y-4">
          <div>
            <label htmlFor="firstName" className="block mt-6 pb-2 text-md leading-7 font-sans font-semibold tracking-wide">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              required
              className={inputClasses('firstName')}
              value={formData.firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
            />
            {touched.firstName && errors.firstName && (
              <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className="block mt-6 pb-2 text-md leading-7 font-sans font-semibold tracking-wide">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              required
              className={inputClasses('lastName')}
              value={formData.lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
            />
            {touched.lastName && errors.lastName && (
              <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block mt-6 pb-2 text-md leading-7 font-sans font-semibold tracking-wide">
              Email
            </label>
            <input
              type="email"
              id="email"
              required
              className={inputClasses('email')}
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
            />
            {touched.email && errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="tripDate" className="block mt-6 pb-2 text-md leading-7 font-sans font-semibold tracking-wide">
              Date of Booked Trip
            </label>
            <input
              type="date"
              id="tripDate"
              required
              min={new Date().toISOString().split('T')[0]}
              className={inputClasses('tripDate')}
              value={formData.tripDate}
              onChange={(e) => handleFieldChange('tripDate', e.target.value)}
            />
            {touched.tripDate && errors.tripDate && (
              <p className="mt-1 text-sm text-red-600">{errors.tripDate}</p>
            )}
          </div>

          <div className="pt-6 pb-8">
            <div className="relative flex items-start">
              <div className="flex h-6 items-center">
                <input
                  id="confirmation"
                  name="confirmation"
                  type="checkbox"
                  required
                  className={`h-4 w-4 rounded ${errors.hasConfirmed ? 'border-red-300' : 'border-gray-300'}`}
                  checked={formData.hasConfirmed}
                  onChange={(e) => handleFieldChange('hasConfirmed', e.target.checked)}
                />
              </div>
              <div className="ml-3 text-sm leading-6">
                <label htmlFor="confirmation" className="mt-4 text-md font-sans tracking-wide">
                  I confirm that I have read, understood and agree to all the terms of the Under SXM Release and Waiver. I acknowledge the risks involved and accept them voluntarily.
                </label>
                {touched.hasConfirmed && errors.hasConfirmed && (
                  <p className="mt-1 text-sm text-red-600">{errors.hasConfirmed}</p>
                )}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={!isFormValid || status === 'submitting'}
            className={`border-solid border gap-4 px-9 py-4 text-sm text-center text-white font-sans tracking-[0.25em] uppercase transition duration-500 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-highlight-500 mx-auto block w-auto min-w-[300px] max-w-60 rounded-se-[0.75em] rounded-es-[0.75em] 
              ${isFormValid ? 'border-primary-500 hover:bg-highlight-500 hover:border-highlight-500 bg-primary-500' : 'bg-gray-300 cursor-not-allowed'}
              ${status === 'submitting' ? 'opacity-75 cursor-not-allowed' : ''}`}
          >
            {status === 'submitting' ? 'Submitting...' : 'Sign Waiver'}
          </button>

          {status === 'error' && (
            <p className="text-red-600 text-sm">Something went wrong. Please try again.</p>
          )}
        </form>
      )}
    </div>
  );
} 